import { useCallback } from 'react';
import { useUpdateTaskMutation } from './mutations/use-update-task-mutation';
import { useUser } from '@/hooks/auth/use-user';
import { ContentType } from '@/utils/task-content-detection';
import { Task } from '@/types/tasks';

/**
 * Hook for tracking generated content in tasks
 * Uses existing task mutation patterns for consistency and reliability
 */
export const useTaskContentTracking = () => {
  const { user } = useUser();
  const updateTaskMutation = useUpdateTaskMutation(user?.id);

  const updateTaskWithGeneratedContent = useCallback(async (
    taskId: string,
    contentType: ContentType,
    contentId: string
  ): Promise<void> => {
    console.log(`[TaskContentTracking] Starting update for task ${taskId}`);
    console.log(`[TaskContentTracking] Content type: ${contentType}`);
    console.log(`[TaskContentTracking] Content ID: ${contentId}`);
    console.log(`[TaskContentTracking] User ID: ${user?.id}`);
    
    // Build the update object with the appropriate field
    const updates: Partial<Task> = {};
    
    switch (contentType) {
      case 'document':
        updates.generated_document_id = contentId;
        break;
      case 'image':
        updates.generated_image_id = contentId;
        break;
      case 'research':
        updates.generated_research_id = contentId;
        break;
      default:
        throw new Error(`Unknown content type: ${contentType}`);
    }

    console.log(`[TaskContentTracking] Updates object (UI format):`, updates);

    try {
      // Use existing mutation with silent mode to avoid toast notifications
      await updateTaskMutation.mutateAsync({
        taskId,
        updates,
        silent: true
      });

      console.log(`[TaskContentTracking] ✅ Successfully linked task ${taskId} to ${contentType}: ${contentId}`);
    } catch (error) {
      console.error(`[TaskContentTracking] ❌ Failed to link task ${taskId} to ${contentType}:`, error);
      console.error(`[TaskContentTracking] Error details:`, {
        taskId,
        contentType,
        contentId,
        updates,
        userId: user?.id
      });
      throw error;
    }
  }, [updateTaskMutation, user?.id]);

  return {
    updateTaskWithGeneratedContent,
    isUpdating: updateTaskMutation.isPending,
  };
}; 